import { useMessage } from '@/providers';
import { getPdfPages, logger } from '@/utils';
import { useCallback } from 'react';
import { type FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import {
  MAXIMUM_FILE_SIZE,
  MAXIMUM_FILE_SIZE_IN_MB,
  MAXIMUM_PDF_FILE_SIZE,
  MAXIMUM_PDF_FILE_SIZE_IN_MB,
  MAXIMUM_PDF_PAGE_COUNT,
} from '../constants';

export type StoredFiles = Array<{
  name: string;
  url: string | null;
  fileContentType: string;
}>;

type Args = {
  files: File[];
  onChange: (acceptedFiles: File[]) => void;
  acceptableImage?: boolean;
  disabled?: boolean;
  maxFiles?: number;
  acceptablePdf?: boolean;
  acceptableExcel?: boolean;
  storedFiles?: StoredFiles;
  enableMaxFileCountControl?: boolean;
};
export const useDndFileUploader = ({
  files,
  onChange,
  acceptableImage = false,
  disabled = false,
  maxFiles = 5,
  acceptablePdf = false,
  acceptableExcel = false,
  storedFiles,
  enableMaxFileCountControl = false,
}: Args) => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();

  const isValidFileType = useCallback((file: File) => {
    return (
      file.type.startsWith('image/') ||
      // https://www.10nin.com/crossplatform/windows/csv/2020/10/30/CrossBrowserCSV.html
      file.type === 'text/csv' ||
      file.type === 'application/octet-stream' ||
      file.type === 'application/vnd.ms-excel' ||
      file.type === 'application/pdf' ||
      file.name.endsWith('.docx') ||
      file.name.endsWith('.xlsx')
    );
  }, []);

  const isDuplicateFile = useCallback(
    (file: File, convertedFileNames: string[]) => {
      if (file.type === 'application/pdf') {
        return (
          files.some(
            (holdFile) =>
              convertedFileNames.includes(holdFile.name) &&
              holdFile.type === 'image/png',
          ) ||
          (storedFiles ?? []).some(
            (holdFile) =>
              convertedFileNames.includes(holdFile.name) &&
              holdFile.fileContentType === 'image/png',
          )
        );
      }
      return (
        files.some(
          (holdFile) =>
            file.name === holdFile.name &&
            file.size === holdFile.size &&
            file.type === holdFile.type &&
            file.lastModified === holdFile.lastModified,
        ) ||
        (storedFiles ?? []).some(
          (holdFile) =>
            file.name === holdFile.name &&
            file.type === holdFile.fileContentType,
        )
      );
    },
    [files, storedFiles],
  );

  const onDropRejected = useCallback(
    (rejectedFiles: FileRejection[]) => {
      const invalidFiles: FileRejection[] = [];

      for (const file of rejectedFiles) {
        invalidFiles.push(file);
      }

      if (invalidFiles.length >= 1) {
        openMessage('error', t('message-maximum-file-upload-limit-exceed'));
      }
    },
    [openMessage, t],
  );

  const onDrop = useCallback(
    async <T extends File>(acceptedFiles: T[]) => {
      const attachableFiles: File[] = [];
      const invalidTypeFiles: File[] = [];
      const invalidSizeFiles: File[] = [];
      const invalidPdfSizeFiles: File[] = [];
      const invalidPdfPageSizeFiles: File[] = [];
      const convertedFileNames: string[] = [];

      for (const file of acceptedFiles) {
        // Check file type
        if (!isValidFileType(file)) {
          invalidTypeFiles.push(file);
          continue;
        }

        // Check file size
        if (file.type !== 'application/pdf' && file.size > MAXIMUM_FILE_SIZE) {
          invalidSizeFiles.push(file);
          continue;
        }

        if (file.type === 'application/pdf') {
          const arrayBuffer = await file.arrayBuffer();
          const pdfTotalPages = await getPdfPages(arrayBuffer);

          for (let i = 1; i <= pdfTotalPages; i++) {
            const fileName = file.name.split('.')[0] ?? 'image';
            convertedFileNames.push(`${fileName}_image_${i}.png`);
          }

          if (file.size > MAXIMUM_PDF_FILE_SIZE) {
            invalidPdfSizeFiles.push(file);
            continue;
          }
          if (pdfTotalPages > MAXIMUM_PDF_PAGE_COUNT) {
            invalidPdfPageSizeFiles.push(file);
            continue;
          }
        }

        const isSameFileOnHold = isDuplicateFile(file, convertedFileNames);
        if (isSameFileOnHold) {
          continue;
        }

        attachableFiles.push(file);
      }

      try {
        if (invalidTypeFiles.length >= 1) {
          await openMessage(
            'error',
            t('message-including-invalid-file-type-with-name', {
              name: invalidTypeFiles.map((file) => file.name).join(', '),
            }),
          );
        }

        if (invalidSizeFiles.length >= 1) {
          await openMessage(
            'error',
            t('message-including-invalid-file-size-with-name', {
              size: `${MAXIMUM_FILE_SIZE_IN_MB}MB`,
              name: invalidSizeFiles.map((file) => file.name).join(', '),
            }),
          );
        }

        if (invalidPdfSizeFiles.length >= 1) {
          await openMessage(
            'error',
            t('message-including-invalid-file-size-with-name', {
              size: `${MAXIMUM_PDF_FILE_SIZE_IN_MB}MB`,
              name: invalidPdfSizeFiles.map((file) => file.name).join(', '),
            }),
          );
        }

        if (invalidPdfPageSizeFiles.length >= 1) {
          await openMessage(
            'error',
            t('message-including-invalid-file-page-count-with-name', {
              pageCount: MAXIMUM_PDF_PAGE_COUNT,
              name: invalidPdfPageSizeFiles.map((file) => file.name).join(', '),
            }),
          );
        }

        if (enableMaxFileCountControl) {
          if (
            attachableFiles.length + files.length + (storedFiles?.length ?? 0) >
            maxFiles
          ) {
            await openMessage(
              'error',
              t('message-maximum-file-upload-limit-exceed'),
            );
            return;
          }
        }
        onChange(attachableFiles);
      } catch (err) {
        logger.error('failed to upload file', err);
      }
    },
    [
      isValidFileType,
      isDuplicateFile,
      onChange,
      openMessage,
      t,
      enableMaxFileCountControl,
      maxFiles,
      storedFiles,
      files,
    ],
  );

  const {
    open: openDropzone,
    getRootProps,
    getInputProps,
  } = useDropzone({
    maxFiles,
    multiple: true,
    noClick: true,
    onDrop,
    onDropRejected,
    disabled,
    accept: {
      ...(acceptableImage
        ? {
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
          }
        : {}),
      ...(acceptablePdf
        ? {
            'application/pdf': ['.pdf'],
          }
        : {}),
      ...(acceptableExcel
        ? {
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
              ['.xlsx'],
          }
        : {}),
    },
  });

  return { openDropzone, getRootProps, getInputProps };
};
